export const profileSubscriptionRoleOrder = ["player", "organiser", "coach", "centre"];

export const profileSubscriptionPlans = {
  player: {
    role: "player",
    roleLabel: "Player",
    freeName: "Player Free",
    proName: "Player Pro",
    planId: "player_pro",
    headline: "Build a player profile that keeps working between games.",
    freeSummary: "Create a public player profile, join games and keep bookings in one account.",
    proSummary: "Add richer proof, clearer availability and stronger profile controls.",
    monthlyCents: 799,
    annualCents: 7900,
    currency: "aud",
    freeFeatures: [
      "Public player profile and local discovery",
      "Court bookings, joined groups and payment status",
      "Basic availability and playing preferences",
      "Up to 4 approved photos or video links"
    ],
    proFeatures: [
      "Up to 20 approved photos and video links",
      "Up to 10 social profile links",
      "Four times the free media allowance",
      "More room for match clips and playing proof",
      "Pro badge on the public player profile"
    ],
    freeEntitlements: {
      profileMediaLimit: 4,
      socialLinkLimit: 3,
      proBadge: false
    },
    proEntitlements: {
      profileMediaLimit: 20,
      socialLinkLimit: 10,
      proBadge: true
    }
  },
  organiser: {
    role: "organiser",
    roleLabel: "Social group",
    freeName: "Group Free",
    proName: "Group Pro",
    planId: "organiser_pro",
    headline: "Run repeat social badminton without chasing every player.",
    freeSummary: "Publish a group, invite players and manage a small number of upcoming sessions.",
    proSummary: "Operate recurring sessions, memberships and member payments from one workspace.",
    monthlyCents: 2499,
    annualCents: 24900,
    currency: "aud",
    freeFeatures: [
      "Public social group profile",
      "Member invitations and platform player search",
      "Up to 4 active dated sessions",
      "Basic attendance and message history"
    ],
    proFeatures: [
      "Unlimited active sessions and recurring schedules",
      "Paid sessions with checkout or direct settlement by country",
      "Paid memberships where online payouts are supported",
      "Up to 200 member invitations",
      "Up to 30 approved photos and video links",
      "Pro badge on the public group profile"
    ],
    freeEntitlements: {
      activeSessionLimit: 4,
      inviteLimit: 25,
      profileMediaLimit: 6,
      paidMemberships: false,
      paidSessions: false,
      proBadge: false
    },
    proEntitlements: {
      activeSessionLimit: -1,
      inviteLimit: 200,
      profileMediaLimit: 30,
      paidMemberships: true,
      paidSessions: true,
      proBadge: true
    }
  },
  coach: {
    role: "coach",
    roleLabel: "Coach",
    freeName: "Coach Free",
    proName: "Coach Pro",
    planId: "coach_pro",
    headline: "Turn coaching enquiries into confirmed lessons and repeat students.",
    freeSummary: "Publish a coaching profile, rates, locations and weekly availability.",
    proSummary: "Manage lesson requests, payments, richer media and coaching operations.",
    monthlyCents: 1999,
    annualCents: 19900,
    currency: "aud",
    freeFeatures: [
      "Public coach profile and local discovery",
      "Rates, locations and weekly availability",
      "Player enquiries and messages",
      "Up to 5 approved photos or video links"
    ],
    proFeatures: [
      "Paid lesson requests with country-aware settlement",
      "Accept, reschedule and complete lesson workflow",
      "Up to 20 approved photos and video links",
      "Up to 10 coaching social links",
      "Pro badge on the public coach profile"
    ],
    freeEntitlements: {
      profileMediaLimit: 5,
      socialLinkLimit: 3,
      paidLessons: false,
      proBadge: false
    },
    proEntitlements: {
      profileMediaLimit: 20,
      socialLinkLimit: 10,
      paidLessons: true,
      proBadge: true
    }
  },
  centre: {
    role: "centre",
    roleLabel: "Centre",
    freeName: "Centre Free",
    proName: "Centre Pro",
    planId: "centre_pro",
    headline: "Make courts easier to sell, fill and manage.",
    freeSummary: "Claim or create a centre and keep its public listing discoverable for free.",
    proSummary: "Unlock the booking calendar, court operations, memberships, centre events and payments.",
    monthlyCents: 9900,
    annualCents: 99000,
    currency: "aud",
    freeFeatures: [
      "Owner-managed public listing and local discovery",
      "Address, contact details, opening hours and facilities",
      "Basic court count and standard fee information",
      "Public messages and centre enquiries"
    ],
    proFeatures: [
      "Online court booking calendar and slot controls",
      "Unlimited published court inventory",
      "Peak, off-peak and weekend fee schedules",
      "Court checkout or pay-at-venue settlement by country",
      "Memberships and credit packs where payouts are supported",
      "Centre-run tournaments, clinics and public events",
      "Up to 40 approved venue photos or video links",
      "Pro badge on the public centre profile"
    ],
    freeEntitlements: {
      publishedCourtLimit: 4,
      profileMediaLimit: 8,
      advancedCourtPricing: false,
      inPlatformBookings: false,
      membershipProducts: false,
      managedEvents: false,
      proBadge: false
    },
    proEntitlements: {
      publishedCourtLimit: -1,
      profileMediaLimit: 40,
      advancedCourtPricing: true,
      inPlatformBookings: true,
      membershipProducts: true,
      managedEvents: true,
      proBadge: true
    }
  }
};

export const getProfileSubscriptionPlan = (role = "player") => (
  profileSubscriptionPlans[role] || profileSubscriptionPlans.player
);

export const profileSubscriptionActiveStatuses = new Set(["active", "trialing"]);

export const isProfileSubscriptionActive = (subscription = {}) => (
  subscription.active === true
  || subscription.entitlementsActive === true
  || profileSubscriptionActiveStatuses.has(String(subscription.status || "").toLowerCase())
);

export const accountProfileSubscription = (accountProfile = {}, role = "player") => {
  const summary = accountProfile.profileSubscriptions?.[role] || {};
  const plan = getProfileSubscriptionPlan(role);
  const active = isProfileSubscriptionActive(summary);
  return {
    ...summary,
    role,
    planId: active ? plan.planId : "free",
    tier: active ? "pro" : "free",
    status: String(summary.status || (active ? "active" : "free")).toLowerCase(),
    active,
    entitlements: active ? plan.proEntitlements : plan.freeEntitlements
  };
};

export const profilePlanAnnualSavingCents = (role = "player") => {
  const plan = getProfileSubscriptionPlan(role);
  return Math.max(0, plan.monthlyCents * 12 - plan.annualCents);
};
